import { useEffect, useState } from "react";

type UseFrameAnimationOptions = {
  /** Number of frames to cycle through */
  frameCount: number;
  /** Interval in milliseconds between frames */
  interval: number;
  /** Start from a random frame index */
  randomStart?: boolean;
};

/**
 * Hook for cycling through animation frames at a given interval.
 * Returns the current frame index.
 */
export function useFrameAnimation({
  frameCount,
  interval,
  randomStart = false,
}: UseFrameAnimationOptions): number {
  const [index, setIndex] = useState(() =>
    randomStart ? Math.floor(Math.random() * frameCount) : 0
  );

  useEffect(() => {
    setIndex((i) => (frameCount === 0 ? 0 : Math.min(i, frameCount - 1)));
  }, [frameCount]);

  useEffect(() => {
    if (frameCount === 0) {
      return;
    }
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % frameCount);
    }, interval);
    return () => clearInterval(timer);
  }, [frameCount, interval]);

  return frameCount === 0 ? 0 : index % frameCount;
}
